import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    Paper
} from '@mui/material'
import moment from 'moment'
import { WEEKDAY_MAPPINGS } from '../../constants'

// Constants
const INVALID_NUMBER_FORMAT_ERROR = '無効な数値形式です。正しい形式: 1,234 または 1234'
const VALIDATION_DEBOUNCE_DELAY = 300 // milliseconds

// Helper function to generate bulk error message
const generateBulkErrorMessage = (invalidLines) => {
    const invalidLinesText = invalidLines.map(item => `行 ${item.row}: "${item.line}"`).join('\n')
    return `無効なデータが見つかりました:\n${invalidLinesText}\n\n空白なしの数値のみを入力してください。`
}

const SalesDataTable = ({ 
    calendarYearMonth, 
    salePreds = {}, 
    mpPreds = {},
    customerPreds = {}, 
    // prevYearSales = {},
    onCustomerPredsChange,
    onMemoChange,
    memos = {},
    onSaveCustomerPreds,
    onBulkSaveCustomerPreds,
    monthlySalesPredTotal,
    monthlyMpPredsTotal = 0,
    monthlyCustomerPredsTotal,
    // monthlyPrevYearSalesTotal
}) => {
    const [dates, setDates] = useState([])
    const [validationErrors, setValidationErrors] = useState({})
    const [tempInputs, setTempInputs] = useState({})
    const inputRefs = useRef({})
    const validationTimeouts = useRef({})
    const originalMemos = useRef({})
    const originalPredictions = useRef({})

    const toPredictionNumber = (value) => {
        if (value == null || value === '') return 0
        if (typeof value === 'number') return Number.isNaN(value) ? 0 : value
        // customerPreds may hold locale-formatted strings like "2,000" after blur
        return parseNumberWithLocale(String(value))
    }

    const getBaselinePrediction = (date) => {
        if (Object.prototype.hasOwnProperty.call(originalPredictions.current, date)) {
            return toPredictionNumber(originalPredictions.current[date])
        }
        return toPredictionNumber(customerPreds[date])
    }

    const hasPredictionChanged = (date, value) => {
        return parseNumberWithLocale(value) !== getBaselinePrediction(date)
    }

    const markPredictionSaved = (date, numericValue) => {
        originalPredictions.current[date] = numericValue
    }

    // Helper function to clear validation error for a specific date
    const clearValidationError = (date) => {
        if (validationErrors[date]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev }
                delete newErrors[date]
                return newErrors
            })
        }
    }

    // Helper function to clear temporary input for a specific date
    const clearTempInput = (date) => {
        if (tempInputs[date] !== undefined) {
            setTempInputs(prev => {
                const newTemp = { ...prev }
                delete newTemp[date]
                return newTemp
            })
        }
    }

    // Helper function to clear validation timeout for a specific date
    const clearValidationTimeout = (date) => {
        if (validationTimeouts.current[date]) {
            clearTimeout(validationTimeouts.current[date])
            delete validationTimeouts.current[date]
        }
    }

    // Debounced validation function
    const debouncedValidate = useCallback((date, value) => {
        // Clear existing timeout for this date
        if (validationTimeouts.current[date]) {
            clearTimeout(validationTimeouts.current[date])
        }

        // Set new timeout for validation
        validationTimeouts.current[date] = setTimeout(() => {
            if (validatePartialInput(value)) {
                // Clear validation error if input becomes valid
                clearValidationError(date)
                
                // Update the actual value if it's a complete valid number
                if (validateNumberString(value)) {
                    onCustomerPredsChange(date, value)
                }
            } else {
                // Set validation error for invalid input
                setValidationErrors(prev => ({
                    ...prev,
                    [date]: INVALID_NUMBER_FORMAT_ERROR
                }))
            }
            
            // Clean up timeout reference
            delete validationTimeouts.current[date]
        }, VALIDATION_DEBOUNCE_DELAY)
    }, [onCustomerPredsChange])

    // Cleanup timeouts on component unmount
    useEffect(() => {
        return () => {
            Object.values(validationTimeouts.current).forEach(timeout => {
                clearTimeout(timeout)
            })
        }
    }, [])

    useEffect(() => {
        if (calendarYearMonth && moment(calendarYearMonth, 'YYYY-MM', true).isValid()) {
            const daysInMonth = moment(calendarYearMonth, 'YYYY-MM').daysInMonth()
            if (daysInMonth) {
                const monthDates = Array.from(Array(daysInMonth).keys()).map(day => {
                    const dateStr = calendarYearMonth + '-' + String(day + 1).padStart(2, '0')
                    return moment(dateStr, 'YYYY-MM-DD').format('YYYY-MM-DD')
                })
                setDates(monthDates)
            } else {
                setDates([])
            }
        } else {
            setDates([])
        }
    }, [calendarYearMonth])

    const formatDate = (date) => {
        if (!date) return ''
        const dateMoment = moment(date, 'YYYY-MM-DD', true)
        if (!dateMoment.isValid()) {
            return ''
        }
        const day = dateMoment.format('D')
        const weekday = dateMoment.format('dddd').toLowerCase()
        return `${day} (${WEEKDAY_MAPPINGS[weekday] || weekday})`
    }

    const formatCurrency = (value) => {
        return value ? value.toLocaleString('ja-JP') : '0'
    }

    // Helper function to parse a locale-formatted number string to integer (ja-JP)
    const parseNumberWithLocale = (value) => {
        if (!value || typeof value !== 'string') return 0
        
        // Remove all grouping separators for ja-JP (comma)
        const normalized = value.replace(/,/g, '').trim()
        
        // Check if the normalized string is a valid number
        if (!/^\d+$/.test(normalized)) return 0
        
        const num = Number(normalized)
        return Number.isNaN(num) ? 0 : num
    }

    // Helper function to format a number to locale string (ja-JP)
    const formatNumberWithLocale = (value) => {
        if (!value && value !== 0) return ''
        
        let num
        if (typeof value === 'string') {
            num = parseNumberWithLocale(value)
        } else {
            num = Number(value)
        }
        
        if (Number.isNaN(num)) return ''
        
        return num.toLocaleString('ja-JP')
    }

    // Helper function to validate common comma-related patterns
    const validateCommaPatterns = (value) => {
        // Check for consecutive commas or commas at the beginning/end
        if (value.includes(',,') || value.startsWith(',') || value.endsWith(',')) {
            return false
        }
        return true
    }

    // Helper function to validate if a string can be parsed as a number
    const validateNumberString = (value) => {
        if (!value || value === '') return true
        
        // Allow only digits and commas
        if (!/^[0-9,]*$/.test(value)) return false
        
        // Check for common comma patterns
        if (!validateCommaPatterns(value)) return false
        
        // Check if the normalized value (without commas) is a valid number
        const normalized = value.replace(/,/g, '')
        if (normalized === '') return true // Allow empty string
        
        return /^\d+$/.test(normalized)
    }

    // Helper function to validate partial input (more permissive for typing)
    const validatePartialInput = (value) => {
        if (!value || value === '') return true
        
        // Allow only digits and commas
        if (!/^[0-9,]*$/.test(value)) return false
        
        // Check for common comma patterns
        if (!validateCommaPatterns(value)) return false
        
        // Allow partial comma-separated numbers during typing
        // Examples: "1", "12", "123", "1,", "12,", "123,", "1,2", "1,23", "1,234", "12,3", "12,34", "12,345"
        const parts = value.split(',')
        if (parts.length > 1) {
            // Check that all parts except the last are 1-3 digits
            for (let i = 0; i < parts.length - 1; i++) {
                if (parts[i].length === 0 || parts[i].length > 3 || !/^\d+$/.test(parts[i])) {
                    return false
                }
            }
            // Last part should be 0-3 digits (0 means user just typed a comma)
            if (parts[parts.length - 1] && (parts[parts.length - 1].length > 3 || !/^\d+$/.test(parts[parts.length - 1]))) {
                return false
            }
        }
        
        return true
    }

    const focusNextInput = (currentDate, offset = 1) => {
        const currentIndex = dates.indexOf(currentDate)
        const nextDate = dates[currentIndex + offset]
        
        if (nextDate && inputRefs.current[nextDate]) {
            setTimeout(() => {
                inputRefs.current[nextDate].focus()
                inputRefs.current[nextDate].select()
            }, 100)
        }
    }

    const handleSingleValuePaste = (pastedText, date) => {
        const numericValue = parseNumberWithLocale(pastedText)
        if (numericValue > 0) {
            // Clear any pending validation timeout
            clearValidationTimeout(date)
            
            // Clear any validation errors for this date
            clearValidationError(date)
            
            // Clear temporary input
            clearTempInput(date)
            
            onCustomerPredsChange(date, numericValue.toString())
            onSaveCustomerPreds(date, 'prediction', numericValue)
            // Update baseline so the blur from focusNextInput does not double-save
            markPredictionSaved(date, numericValue)
            focusNextInput(date)
        }
    }

    const handleMultiValuePaste = (lines, startDate) => {
        const currentIndex = dates.indexOf(startDate)
        const predictionsData = []
        const invalidLines = []
        
        lines.forEach((line, index) => {
            const targetDate = dates[currentIndex + index]
            if (targetDate) {
                const trimmedLine = line.trim()
                // Skip empty lines
                if (trimmedLine === '') {
                    return
                }
                
                if (!validateNumberString(trimmedLine)) {
                    invalidLines.push({ line: trimmedLine, row: index + 1 })
                } else {
                    const numericValue = parseNumberWithLocale(trimmedLine)
                    const finalValue = numericValue.toString()
                    
                    // Clear any pending validation timeout
                    clearValidationTimeout(targetDate)
                    
                    // Clear any validation errors for this date
                    clearValidationError(targetDate)
                    
                    // Clear temporary input
                    clearTempInput(targetDate)
                    
                    predictionsData.push({
                        date: targetDate,
                        prediction: finalValue
                    })
                }
            }
        })
        
        if (invalidLines.length > 0) {
            const errorMessage = generateBulkErrorMessage(invalidLines)
            alert(errorMessage)
            return
        }
        
        if (predictionsData.length > 0) {
            predictionsData.forEach(({ date, prediction }) => {
                onCustomerPredsChange(date, prediction)
                // Update baselines so blur from focusNextInput does not double-save
                markPredictionSaved(date, parseNumberWithLocale(prediction))
            })
            onBulkSaveCustomerPreds(predictionsData)
        }
        
        focusNextInput(startDate, lines.length)
    }

    const handleKeyDown = (e, date) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            const value = e.target.value
            
            // Clear any pending validation timeout
            clearValidationTimeout(date)
            
            // Validate the final value
            if (value && !validateNumberString(value)) {
                setValidationErrors(prev => ({
                    ...prev,
                    [date]: INVALID_NUMBER_FORMAT_ERROR
                }))
                return
            }
            
            // Clear validation error if valid
            clearValidationError(date)
            
            const numericValue = parseNumberWithLocale(value)
            // Only persist when the amount actually changed
            if (hasPredictionChanged(date, value)) {
                onCustomerPredsChange(date, String(numericValue))
                clearTempInput(date)
                onSaveCustomerPreds(date, 'prediction', numericValue)
                markPredictionSaved(date, numericValue)
            }
            focusNextInput(date)
        } else if (e.key === 'Tab') {
            const value = e.target.value
            
            // Clear any pending validation timeout
            clearValidationTimeout(date)
            
            // Validate the final value
            if (value && !validateNumberString(value)) {
                setValidationErrors(prev => ({
                    ...prev,
                    [date]: INVALID_NUMBER_FORMAT_ERROR
                }))
                e.preventDefault()
                return
            }
            
            // Clear validation error if valid
            clearValidationError(date)
            
            const numericValue = parseNumberWithLocale(value)
            // Only persist when the amount actually changed (Tab with no edit must not save)
            if (hasPredictionChanged(date, value)) {
                onCustomerPredsChange(date, String(numericValue))
                clearTempInput(date)
                onSaveCustomerPreds(date, 'prediction', numericValue)
                markPredictionSaved(date, numericValue)
            }
        }
    }
    const headerCellStyle = {
        backgroundColor: '#e5f6fd',
        fontWeight: 'bold',
        borderRight: '1px solid lightgray',
        fontSize: '1.2rem'
    }
    const bodyCellStyle = {
        borderRight: '1px solid lightgray',
        textAlign: 'right',
        fontSize: '1.2rem'
    }
    return (
        <TableContainer 
            component={Paper}
            sx={{
                border: '1px solid lightgray',
                maxHeight: 750,
                maxWidth: '97%',
                marginLeft: '1rem',
                overflow: 'auto'
            }}
        >
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ ...headerCellStyle, minWidth: '3rem' }}>
                            日付
                        </TableCell>
                        {/* <TableCell sx={{ ...headerCellStyle, minWidth: '150px' }}>
                            前年売上 ({monthlyPrevYearSalesTotal.toLocaleString('ja-JP') + '円'})
                        </TableCell> */}
                        <TableCell sx={{ ...headerCellStyle, minWidth: '200px' }}>
                            AI予測売上 ({monthlySalesPredTotal.toLocaleString('ja-JP') + '円'})
                        </TableCell>
                        <TableCell sx={{ ...headerCellStyle, minWidth: '200px' }}>
                            MP売上高 ({monthlyMpPredsTotal.toLocaleString('ja-JP') + '円'})
                        </TableCell>
                        <TableCell sx={{ ...headerCellStyle, minWidth: '150px' }}>
                            予定 ({monthlyCustomerPredsTotal.toLocaleString('ja-JP') + '円'})
                        </TableCell>
                        <TableCell sx={{ ...headerCellStyle, minWidth: '500px' }}>
                            メモ
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {dates.map((date) => (
                        <TableRow sx={{ height: '30px'}} key={date}>
                            <TableCell sx={{...bodyCellStyle, textAlign: 'center'}}>
                                {formatDate(date)}
                            </TableCell>
                            {/* <TableCell sx={bodyCellStyle}>
                                {formatCurrency(prevYearSales[date])}
                            </TableCell> */}
                            <TableCell sx={bodyCellStyle}>
                                {formatCurrency(salePreds[date])}
                            </TableCell>
                            <TableCell sx={bodyCellStyle}>
                                {formatCurrency(mpPreds[date])}
                            </TableCell>
                            <TableCell sx={bodyCellStyle}>
                                <TextField
                                    ref={(el) => {
                                        if (el) {
                                            inputRefs.current[date] = el.querySelector('input')
                                        }
                                    }}
                                    variant="outlined"
                                    size="small"
                                    value={tempInputs[date] !== undefined ? tempInputs[date] : formatNumberWithLocale(customerPreds[date])}
                                    onFocus={(e) => {
                                        e.target.select()
                                        // Capture baseline so Tab/blur without edits do not save
                                        originalPredictions.current[date] = toPredictionNumber(customerPreds[date])
                                    }}
                                    error={!!validationErrors[date]}
                                    helperText={validationErrors[date] || ''}
                                    onChange={(e) => {
                                        const value = e.target.value
                                        
                                        // Always allow the input to be updated for better UX
                                        setTempInputs(prev => ({ ...prev, [date]: value }))
                                        
                                        // Debounce validation
                                        debouncedValidate(date, value)
                                    }}
                                    onBlur={(e) => {
                                        const value = e.target.value
                                        
                                        // Clear any pending validation timeout
                                        clearValidationTimeout(date)
                                        
                                        // Clear temporary input
                                        clearTempInput(date)
                                        
                                        // Validate on blur immediately
                                        if (value && !validateNumberString(value)) {
                                            setValidationErrors(prev => ({
                                                ...prev,
                                                [date]: INVALID_NUMBER_FORMAT_ERROR
                                            }))
                                        } else {
                                            // Clear validation error if input is valid
                                            clearValidationError(date)

                                            // Only update local state + persist when the amount actually changed.
                                            // Always store a plain numeric string (never "3,210") so baselines
                                            // stay parseable, including when the field is cleared to 0.
                                            if (validateNumberString(value) && hasPredictionChanged(date, value)) {
                                                const numericValue = parseNumberWithLocale(value)
                                                onCustomerPredsChange(date, String(numericValue))
                                                onSaveCustomerPreds(date, 'prediction', numericValue)
                                                markPredictionSaved(date, numericValue)
                                            }
                                        }

                                        delete originalPredictions.current[date]
                                    }}
                                    onKeyDown={(e) => handleKeyDown(e, date)}
                                    onPaste={(e) => {
                                        e.preventDefault()
                                        const pastedText = e.clipboardData.getData('text')
                                        const lines = pastedText.split(/\r?\n/)
                                        
                                        if (lines.length === 1) {
                                            handleSingleValuePaste(pastedText, date)
                                        } else {
                                            handleMultiValuePaste(lines, date)
                                        }
                                    }}
                                    InputProps={{
                                        inputProps: {
                                            style: { textAlign: 'right', fontSize: '1.2rem' }
                                        }
                                    }}
                                    sx={{ width: '100%' }}
                                />
                            </TableCell>
                            <TableCell sx={{ borderRight: '1px solid lightgray' }}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    value={memos[date] || ''}
                                    onChange={(e) => onMemoChange(date, e.target.value)}
                                    onFocus={(e) => {
                                        e.target.select()
                                        // Store the original memo value to detect changes on blur
                                        originalMemos.current[date] = memos[date] || ''
                                    }}
                                    onBlur={(e) => {
                                        const value = e.target.value
                                        const originalValue = originalMemos.current[date] || ''
                                        
                                        // Only save if the memo has actually changed
                                        if (value !== originalValue) {
                                            onSaveCustomerPreds(date, 'memo', value)
                                        }
                                        
                                        // Clean up the stored original value
                                        delete originalMemos.current[date]
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                                            e.preventDefault()
                                            const value = e.target.value
                                            const originalValue = originalMemos.current[date] || ''
                                            // Only persist when the memo actually changed
                                            if (value !== originalValue) {
                                                onSaveCustomerPreds(date, 'memo', value)
                                                // Update baseline so the subsequent blur doesn't double-save
                                                originalMemos.current[date] = value
                                            }
                                        }
                                    }}
                                    multiline
                                    maxRows={3}
                                    sx={{ width: '100%', '& .MuiInputBase-input': { fontSize: '1.2rem' } }}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default SalesDataTable 